import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CUSTOM_G1, CUSTOM_G2, CUSTOM_LENGTH, CUSTOM_PART_NUMBER, IS_CUSTOM_PRODUCT, IS_VARIANT_PRODUCT, PARENT_PRODUCT_THUMBNAIL, PARENT_PRODUCT_TITLE, PARENT_PRODUCT_URL, VARIANT_PRODUCT_URL } from "@thkStore/Constants/ProductPropertiesKeys";
import React, { useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    product: CartLineModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    extendedStyles?: ProductDescriptionStyles;
}

type Props = OwnProps;

export interface ProductDescriptionStyles {
    productDetailLink?: LinkPresentationProps;
    productDetailDescription?: TypographyProps;
    parentProductDetailLink?: LinkPresentationProps;
    customPropertiesWrapper?: InjectableCss;
    customPropertyItem?: SmallHeadingAndTextStyles;
}

export const productDescriptionStyles: ProductDescriptionStyles = {
    productDetailLink: {
        css: css`
            font-weight: bold;
            width: 100%;
            padding: 0;
            margin-bottom: 0;
            color: ${({ theme }) => theme.colors.secondary.contrast} !important;
        `,
        typographyProps: {
            css: css`
                width: 100%;
                color: ${({ theme }) => theme.colors.secondary.contrast} !important;
                ${wrapInContainerStyles}
            `,
        },
    },
    productDetailDescription: {
        weight: "bold",
    },
    customPropertiesWrapper: {
        css: css`
            margin: 6px 0 0;
        `,
    },
    parentProductDetailLink: {
        css: css`
            font-size: 22px;
            color: ${({ theme }) => theme.colors.secondary.main};
            margin-bottom: 0;
        `
    },
    customPropertyItem: {
        wrapper: {
            css: css`
                flex-direction: row;
                align-items: baseline;
                gap: 4px;
            `,
        },
        heading: {
            css: css`
                font-weight: normal;
                white-space: nowrap;
                &::after {
                    content: ":";
                }
            `,
        },
    },
};

const CartLineProductDescription = ({ product, onClick, extendedStyles }: Props) => {
    const [styles] = useState(() => mergeToNew(productDescriptionStyles, extendedStyles));
    
    const isCustomProduct = product?.properties?.[IS_CUSTOM_PRODUCT] && JSON.parse(product.properties[IS_CUSTOM_PRODUCT].toLowerCase()) === true;
    const description = isCustomProduct
        ? product.properties[CUSTOM_PART_NUMBER] 
        : product.shortDescription || product.productName;

    const customProductInfo = isCustomProduct ? {
        length: product.properties?.[CUSTOM_LENGTH],
        g1: product.properties?.[CUSTOM_G1],
        g2: product.properties?.[CUSTOM_G2],
    } : {};        

    const additionalProperties = {
        isVariantProduct: (product.properties[IS_VARIANT_PRODUCT] && JSON.parse(product.properties[IS_VARIANT_PRODUCT].toLowerCase()) === true),
        parentProductTitle: product.properties[PARENT_PRODUCT_TITLE],
        parentProductThumbnail: product.properties[PARENT_PRODUCT_THUMBNAIL],
        parentProductUrl: product.properties[PARENT_PRODUCT_URL],
        variantProductUrl: product.properties[VARIANT_PRODUCT_URL],
    };    

    const productDetailPath = additionalProperties.isVariantProduct ? additionalProperties.variantProductUrl : product.productUri;    
    return productDetailPath && (!("isActiveProduct" in product) || product.isActiveProduct) ? (
        <>
            {(additionalProperties.isVariantProduct && additionalProperties.parentProductUrl) && (
                <Link
                    {...styles.parentProductDetailLink}
                    href={additionalProperties.parentProductUrl}
                    alt={`See product details for ${additionalProperties.parentProductTitle}`}
                    onClick={onClick}
                    data-test-selector="parentProductDescriptionLink"
                >
                    {additionalProperties.parentProductTitle}
                </Link>
            )}
            {isCustomProduct
                ? 
                <>
                    <Typography {...styles.productDetailDescription}>{description}</Typography>
                    <StyledWrapper {...styles.customPropertiesWrapper}>
                        <SmallHeadingAndText heading="Length" text={customProductInfo?.length ?? ""} extendedStyles={styles.customPropertyItem} />
                        <SmallHeadingAndText heading="G1" text={customProductInfo?.g1 ?? ""} extendedStyles={styles.customPropertyItem} />
                        <SmallHeadingAndText heading="G2" text={customProductInfo?.g2 ?? ""} extendedStyles={styles.customPropertyItem} />
                    </StyledWrapper>
                </>                    
                :
                <Link
                    {...styles.productDetailLink}
                    href={productDetailPath}
                    alt={`See product details for ${description}`}
                    onClick={onClick}
                    data-test-selector="productDescriptionLink"
                >
                    {description}
                </Link>
            }
        </>
        
    ) : (
        <Typography {...styles.productDetailDescription}>{description}</Typography>
    );
};

export default CartLineProductDescription;
